<template>
  <div class="header__breadcrumbs">
    <BreadcrumbsProjectsBlock
      v-if="displayProjectsBlock && $route.params.projectId"
      :selectedProject="selectedProject"
      @onTeamClick="onTeamClick"
    />
    <BreadcrumbsMyWorkBlock v-if="breadcrumbsMyWorkBlock" />
    <TeamSelectionDropdown
      v-if="displayTeamSelectionBlock"
      :selectedTeam="selectedTeam"
      :teams="teams"
      className="team-selection team-selection-breadcrumb"
    >
      <template #singleLabel="{ option }">
        <BaseSubHeading
          size="large"
          weight="400"
          color="monochrome-05"
          className="header__breadcrumb"
        >
          /<span>{{ option.name }}</span>
        </BaseSubHeading>
      </template>
      <template #option="{ option }">
        <router-link
          v-if="selectedProject.urlName"
          :to="{
            params: {
              teamId: option.urlName,
              projectId: selectedProject.urlName
            },
            name: meta?.navigateToByTeamBreadcrumbs as string
          }"
          class="team-selection-breadcrumb__option"
        >
          <Avatar
            :src="option.picture?.pictureUrl || null"
            size="sm"
            class="multiselect__option__avatar"
            type="teamsGroup"
          />
          <span class="multiselect__option__name">{{ option.name }}</span>
        </router-link>
      </template>
    </TeamSelectionDropdown>
    <BreadcrumbsTeamsBlock
      v-else-if="displayTeamNavigationBlock"
      :teams="teams"
      @onMenuItemClick="onSprintSelected"
    />
    <BaseSubHeading
      v-else-if="selectedTeam?.name && !$route?.meta?.hideTeamSection && canShowTeamList"
      size="large"
      weight="400"
      color="monochrome-05"
      className="header__breadcrumb"
    >
      /<span>{{ selectedTeam.name }}</span>
    </BaseSubHeading>

    <button
      v-if="sprintLabel"
      type="button"
    >
      <BaseSubHeading
        size="large"
        weight="400"
        color="monochrome-05"
        className="header__breadcrumb"
      >
        /<span>{{ sprintLabel }}</span>
      </BaseSubHeading>
    </button>
    <template
      v-for="(breadcrumbConfig, index) in breadcrumbConfigList"
      :key="index"
    >
      <BaseSubHeading
        v-if="
          !isBreadcrumbTitleWaiting(breadcrumbConfig) &&
          (breadcrumbLink(breadcrumbConfig) || breadcrumbTitle(breadcrumbConfig))
        "
        size="large"
        weight="400"
        color="monochrome-05"
        className="header__breadcrumb"
      >
        <template v-if="breadcrumbLink(breadcrumbConfig)"
          >/<router-link :to="breadcrumbLink(breadcrumbConfig)?.route">{{
            breadcrumbLink(breadcrumbConfig)?.title
          }}</router-link></template
        >
        <template v-else
          >/<span>{{ breadcrumbTitle(breadcrumbConfig) }}</span></template
        >
      </BaseSubHeading>
      <component :is="breadcrumbComponent(breadcrumbConfig)" />
    </template>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Watch } from "vue-property-decorator"
import { useAcl } from "vue-simple-acl"
import { Action, Getter } from "s-vuex-class"

import Avatar from "@/components/Avatar/index.vue"
import BaseSubHeading from "@/components/Base/BaseSubHeading.vue"
import BreadcrumbsMyWorkBlock from "@/components/Header/BreadcrumbsMyWorkBlock.vue"
import BreadcrumbsProjectsBlock from "@/components/Header/BreadcrumbsProjectsBlock.vue"
import BreadcrumbsTeamsBlock from "@/components/Header/BreadcrumbsTeamsBlock.vue"
import TeamSelectionDropdown from "@/components/Team/TeamSelectionDropdown.vue"

import { Account, Project, Team, User } from "@/models"
import { BreadcrumbConfig, BreadcrumbLink, BreadcrumbSelectedTeam } from "@/models/MetaConfigs"
import { emitterEmit } from "@/plugins/mitt-emitter"
import { Actions, Getters, store } from "@/store"
// BreadcrumbConfig list components imports
import SupportCaseDropdown from "@/views/Create/CreateItem/components/SupportCaseDropdown.vue"

@Options({
  name: "Breadcrumbs",
  components: {
    BaseSubHeading,
    BreadcrumbsProjectsBlock,
    BreadcrumbsTeamsBlock,
    TeamSelectionDropdown,
    SupportCaseDropdown,
    BreadcrumbsMyWorkBlock,
    Avatar
  }
})
export default class Breadcrumbs extends Vue {
  @Getter(Getters.GET_SESSION_TEAM) sessionTeam: Team
  @Getter(Getters.GET_PROJECT) project: Project
  @Getter(Getters.GET_USER_CURRENT_PROFILE) user: User
  @Getter(Getters.GET_ACCOUNT) readonly account: Account

  @Action(Actions.SET_SESSION_TEAM) setSelectedTeam: (team: Team) => void
  @Action(Actions.RESET_SESSION_TEAM) resetTeam: () => void
  @Action(Actions.GET_PROJECT_TEAMS) getTeamsInProject: (projectId: string) => Promise<Team[]>

  teams: Team[] = []

  get isAdmin() {
    return useAcl().can.any(["admin", "superAdmin"])
  }

  get displayProjectsBlock() {
    return !this.meta.hideProjectLabel
  }

  get projectId() {
    return this.selectedProject.id
  }

  get selectedProject(): Project {
    const breadcrumbSelectedProject = this.$route.meta?.breadcrumbSelectedProject as any
    if (!breadcrumbSelectedProject || !breadcrumbSelectedProject.selectedProject) {
      return this.project
    }
    const action = breadcrumbSelectedProject.selectedProject
    let data = {
      value: breadcrumbSelectedProject.bcGetter
        ? store.getters[Getters[breadcrumbSelectedProject.bcGetter]]
        : "",
      route: this.$route
    }
    const result = action(data)
    return result ? new Project(result) : this.project
  }

  get displayTeamSelectionBlock() {
    return (
      (this.canShowTeamList && this.canSelectTeam && this.teams.length > 1 && this.selectedTeam) ||
      (this.breadcrumbSelectedTeam && this.teams.length > 1)
    )
  }

  get breadcrumbsMyWorkBlock() {
    return this.meta.showMyWorkNavigation
  }

  get displayTeamNavigationBlock() {
    return this.canShowTeamList && this.teams.length > 1 && !this.displayTeamSelectionBlock
  }

  get meta() {
    return this.$route.meta ?? null
  }

  get breadcrumbConfigList(): BreadcrumbConfig[] {
    return (this.meta?.breadcrumbConfigList as BreadcrumbConfig[]) ?? []
  }

  get sprintLabel() {
    if (!this.$route.params?.sprintId || this.$route.meta?.hideSprintBreadcrumb) return ""
    return this.$route.params?.sprintId === "active"
      ? "Active Sprint"
      : `Sprint #${this.$route.params.sprintId}`
  }

  breadcrumbTitle(breadcrumbConfig: BreadcrumbConfig) {
    if (
      !this.isSuperAdmin &&
      this.$route.name === "edit-account" &&
      this.user.accounts.find(account => this.account?.id === account.id)
    ) {
      return "My Account"
    }
    if (typeof breadcrumbConfig.breadcrumbTitle === "function") {
      const action = breadcrumbConfig.breadcrumbTitle
      if (!breadcrumbConfig.bcGetter) {
        return ""
      }
      let data = {
        value: store.getters[Getters[breadcrumbConfig.bcGetter]],
        route: this.$route
      }
      if (this.$route.name === "edit-user" && this.$route.params?.userId === this.user.id) {
        data.value = ""
      }
      const result = action(data)
      return result ? result : ""
    } else {
      return breadcrumbConfig.breadcrumbTitle
    }
  }
  //The configuration is in router.ts
  breadcrumbLink(breadcrumbConfig: BreadcrumbConfig): BreadcrumbLink | undefined {
    if (!breadcrumbConfig.breadcrumbLink) return
    const action = breadcrumbConfig.breadcrumbLink
    let data = {
      //To get the value you need to pass bcGetter in BreadcrumbConfig

      value: breadcrumbConfig.bcGetter ? store.getters[Getters[breadcrumbConfig.bcGetter]] : "",
      route: this.$route
    }
    const result = action(data)
    return result?.title ? result : undefined
  }

  breadcrumbComponent(breadcrumbConfig: BreadcrumbConfig): any {
    if (!breadcrumbConfig.breadcrumbComponent) {
      return
    }
    const action = breadcrumbConfig.breadcrumbComponent
    let data = {
      value: breadcrumbConfig.bcGetter ? store.getters[Getters[breadcrumbConfig.bcGetter]] : "",
      route: this.$route
    }
    const result = action(data)
    return result
  }

  get breadcrumbSelectedTeam(): Team | null {
    const breadcrumbSelectedTeam = this.$route.meta
      ?.breadcrumbSelectedTeam as BreadcrumbSelectedTeam
    if (!breadcrumbSelectedTeam || !breadcrumbSelectedTeam.selectedTeam) {
      return null
    }
    const action = breadcrumbSelectedTeam.selectedTeam
    let data = {
      value: breadcrumbSelectedTeam.bcGetter
        ? store.getters[Getters[breadcrumbSelectedTeam.bcGetter]]
        : "",
      route: this.$route
    }
    const result = action(data)
    return result
  }

  isBreadcrumbTitleWaiting(breadcrumbConfig: BreadcrumbConfig) {
    if (!breadcrumbConfig?.bcWait) return false
    return this.$wait.is(breadcrumbConfig.bcWait as string)
  }

  get isSuperAdmin() {
    return useAcl().can("superAdmin")
  }

  get canSelectTeam() {
    return this.meta?.canSelectTeam
  }

  get canShowTeamList() {
    return !!(
      !this.$route.meta?.hideTeamSection &&
      this.$route.params?.projectId &&
      (this.$route.params?.teamId || this.canSelectTeam)
    )
  }

  get selectedTeam() {
    return this.breadcrumbSelectedTeam ?? this.sessionTeam
  }

  @Watch("projectId")
  async watchProjectId() {
    if (!this.projectId) return
    this.$wait.start("teamsAndProject.get")
    this.teams = await this.getTeamsInProject(this.projectId)
    this.$wait.end("teamsAndProject.get")
  }

  async onSprintSelected({ sprint, team }) {
    await this.$router.push({
      name: "sprint",
      params: { sprintId: sprint.sequenceNumber, teamId: team.urlName }
    })
  }

  onTeamClick(team: Team) {
    this.onSelectTeam(team)
    this.$router.push({
      name: "backlog",
      params: {
        projectId: this.selectedProject.urlName,
        teamId: team.urlName
      }
    })
  }

  async onSelectTeam(team: Team) {
    await this.$router.replace({
      params: {
        teamId: team.urlName,
        projectId: this.selectedProject.urlName
      },
      query: this.$route.query
    })
  }

  @Watch("$route.params.teamId")
  watchTeamId() {
    if (!this.$route.params.teamId) {
      this.resetTeam()
    }
  }
}
</script>
